import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import Dropdown from 'monday-ui-react-core/dist/Dropdown';
import Delete from 'monday-ui-react-core/dist/icons/Delete';
import Add from 'monday-ui-react-core/dist/icons/Add';

import { Controller, useFormContext } from 'react-hook-form';
import Button from 'monday-ui-react-core/dist/Button';

import styled from 'styled-components';

import { getBoardColumnsFromItemId } from '../../services/boards';

import {
  SUBITEM_FILTER_CHAINING,
  DropdownOptionT,
  SUBITEM_FILTER_CONDITIONS,
  EMPTY_UNEMPTY_CONDITIONS,
} from '../../config/subItemFilter.constants';

import { parseStatusColumnOptions } from '../../utils/parse-status-column-options';
import { portalDropdownStyles } from '../DocxTableConfig/constants';

const FilterItemWrapper = styled.div`
  display: flex;
  gap: var(--spacing-xl);
  align-items: center;
  .filter-condition {
    width: 120px;
  }
  .filter-dropdown {
    width: 189px;
  }
  .dd-error {
    border-color: red !important;
  }
`;

const ErrorMesage = styled.p`
  font-size: 12px;
  color: red;
  padding: 0;
  margin: 0;
`;

const AddNewButton = styled(Button)`
  padding: 0;
  width: min-content;
  color: var(--primary-color);
  &:hover {
    background: white !important;
    color: var(--primary-hover-color);
  }
`;

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
`;

const usePreProcessBoardColumn = (boardColumns, filter) => {
  const boardColumnsOptions = (boardColumns ?? [])
    .filter((col) => col.type === 'status')
    .map((column) => ({
      label: column.title,
      value: column.id,
      columnType: column.type,
    }));

  const selectedColumn = (boardColumns ?? []).find(
    (col) => col.id === filter?.targetColumnId,
  );

  const availableConditions = SUBITEM_FILTER_CONDITIONS[selectedColumn?.type];
  const availableValuesOptions = parseStatusColumnOptions(selectedColumn);

  return {
    boardColumnsOptions,
    selectedColumn,
    availableConditions,
    availableValuesOptions,
  };
};

const FilterItem = ({ boardColumns, filter, index, updateFilters }) => {
  const { boardColumnsOptions, availableConditions, availableValuesOptions } =
    usePreProcessBoardColumn(boardColumns, filter);

  const {
    control,
    formState: { errors },
    trigger,
    watch,
  } = useFormContext();

  const filterCondition = watch(`filters.${index}.condition`);

  return (
    <FilterItemWrapper>
      <div className="filter-condition">
        {index === 0 ? (
          <p>Where</p>
        ) : index === 1 ? (
          <Controller
            name={`filterChaining`}
            control={control}
            render={({ field }) => (
              <Dropdown
                className={errors?.filterChaining?.message ? 'dd-error' : ''}
                size={Dropdown.size.MEDIUM}
                placeholder="Filter"
                menuPortalTarget={document.body}
                menuPlacement="auto"
                extraStyles={portalDropdownStyles}
                value={SUBITEM_FILTER_CHAINING.find((opt) => {
                  return opt.value === field.value;
                })}
                options={SUBITEM_FILTER_CHAINING}
                onChange={(value: DropdownOptionT | null) => {
                  field.onChange(value?.value ?? '');
                  trigger('filterChaining');
                }}
              />
            )}
          />
        ) : null}
      </div>
      <Controller
        control={control}
        name={`filters.${index}.targetColumnId`}
        render={() => (
          <Dropdown
            size={Dropdown.size.MEDIUM}
            className={`filter-dropdown ${
              errors?.filters?.[index]?.targetColumnId ? 'dd-error' : ''
            }`}
            placeholder="Column"
            menuPortalTarget={document.body}
            menuPlacement="auto"
            extraStyles={portalDropdownStyles}
            value={boardColumnsOptions.find((opt) => {
              return opt.value === filter.targetColumnId;
            })}
            onChange={(value: (DropdownOptionT & { columnType: string }) | null) => {
              updateFilters('update', {
                index,
                filter: {
                  ...filter,
                  targetColumnId: value?.value ?? '',
                  columnType: value?.columnType ?? '',
                },
              });
              trigger(`filters.${index}.targetColumnId`);
            }}
            options={boardColumnsOptions}
          />
        )}
      />

      <Controller
        name={`filters.${index}.condition`}
        render={() => (
          <Dropdown
            size={Dropdown.size.MEDIUM}
            className={`filter-dropdown ${
              errors?.filters?.[index]?.condition ? 'dd-error' : ''
            }`}
            placeholder="Condition"
            menuPortalTarget={document.body}
            menuPlacement="auto"
            extraStyles={portalDropdownStyles}
            value={availableConditions?.find((opt) => {
              return opt.value === filter.condition;
            })}
            onChange={(value: (DropdownOptionT & { columnType: string }) | null) => {
              updateFilters('update', {
                index,
                filter: {
                  ...filter,
                  condition: value?.value ?? '',
                  ...((!value || EMPTY_UNEMPTY_CONDITIONS.includes(value.value)) && {
                    value: '',
                  }),
                },
              });
              trigger(`filters.${index}.condition`);
            }}
            options={availableConditions}
          />
        )}
      />

      <Controller
        name={`filters.${index}.value`}
        render={() => (
          <Dropdown
            disabled={EMPTY_UNEMPTY_CONDITIONS.includes(filterCondition)}
            placeholder="Value"
            size={Dropdown.size.MEDIUM}
            className={`filter-dropdown ${
              errors?.filters?.[index]?.value ? 'dd-error' : ''
            }`}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            extraStyles={portalDropdownStyles}
            value={availableValuesOptions?.find((opt) => {
              return opt.value === filter.value;
            })}
            onChange={(value: (DropdownOptionT & { columnType: string }) | null) => {
              updateFilters('update', {
                index,
                filter: {
                  ...filter,
                  value: value?.value ?? '',
                },
              });

              trigger(`filters.${index}.value`);
            }}
            options={availableValuesOptions}
          />
        )}
      />

      <Button
        onClick={() => {
          updateFilters('remove', index);
          trigger(`filters.${index}`);
          trigger('filterChaining');
        }}
        leftIcon={Delete}
        kind={Button.kinds.TERTIARY}
        color={Button.colors.NEGATIVE}
      >
        Delete
      </Button>
    </FilterItemWrapper>
  );
};

type FilterT = {
  targetColumnId: string;

  condition: string;

  value: string;

  columnType: string;
};

const DEFAULT_FILTER: FilterT = {
  targetColumnId: '',
  condition: '',
  value: '',
  columnType: '',
};

const SubItemFilter = ({
  updateFilters,
  itemId,
}: {
  updateFilters: (...any) => void;
  itemId?: string | number | null;
}) => {
  const params = useParams();
  const resolvedItemId = itemId ?? params.itemId;

  const {
    watch,
    formState: { errors },
    trigger,
  } = useFormContext();

  const filters = watch('filters');

  const controlledFields =
    filters?.map((field, index) => {
      return {
        ...field,
        ...filters[index],
      };
    }) || [];

  const { data } = useQuery({
    queryKey: ['board-columns', resolvedItemId],
    queryFn: () => getBoardColumnsFromItemId(resolvedItemId, ['status']),
    enabled:
      !!resolvedItemId &&
      resolvedItemId !== 0 &&
      resolvedItemId !== '0',
  });

  return (
    <FilterWrapper itemCount={filters?.length || 0}>
      {controlledFields?.map((filter, index) => (
        <FilterItem
          key={filter._id}
          updateFilters={updateFilters}
          boardColumns={data?.columns || []}
          filter={filter}
          index={index}
        />
      ))}

      {errors?.filters?.message || errors?.filters?.root?.message ? (
        <ErrorMesage>
          {(errors?.filters?.message || errors?.filters?.root?.message) + '*' ||
            ''}
        </ErrorMesage>
      ) : null}

      <AddNewButton
        leftIcon={Add}
        onClick={() => {
          updateFilters('add', DEFAULT_FILTER);
          trigger('filters');
        }}
        kind={Button.kinds.TERTIARY}
        color={Button.colors.PRIMARY}
      >
        Add new
      </AddNewButton>
    </FilterWrapper>
  );
};

export default SubItemFilter;
